import { cx } from '@/lib/cx';
import { barWidth } from '@/lib/commission-progress';

/**
 * A horizontal progress bar for the commission progress strip and its modal.
 *
 * Track and fill match `AttainmentBar` in `TargetTiles.tsx`: a sunken track
 * with an `fg-secondary` fill, both `rounded-xs`, tokens only.
 *
 * - `pct` is the whole-number percentage. `null` means there is no target to
 *   measure against: the track renders empty and `aria-valuenow` is omitted,
 *   so assistive tech reports no value rather than a misleading zero.
 * - The geometry is clamped to 0..100 by `barWidth` (over 100% fills fully,
 *   negative shows an empty fill). `aria-valuenow` is the same clamped figure;
 *   the caller prints the unclamped percentage beside the bar.
 * - `muted` uses `bg-fg-muted`: the index.css theme carries neither an
 *   `fg-tertiary` nor a `border-strong` token.
 */
export function ProgressMeter({
  pct,
  size = 'sm',
  label,
  tone = 'default',
  className,
}: {
  pct: number | null;
  size?: 'sm' | 'lg';
  label: string;
  tone?: 'default' | 'muted';
  className?: string;
}) {
  const width = barWidth(pct);

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      {...(pct === null ? {} : { 'aria-valuenow': Math.round(width) })}
      className={cx(
        'w-full overflow-hidden rounded-xs bg-surface-sunken',
        size === 'lg' ? 'h-2.5' : 'h-1.5',
        className,
      )}
    >
      <div
        className={cx('h-full rounded-xs', tone === 'muted' ? 'bg-fg-muted' : 'bg-fg-secondary')}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}
